import React from 'react';
import { CalendarClock, MapPin, FileText, Trash2, ExternalLink } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

const SLATE = '#3E5C76';
const STONE = '#8C8275';

function formatRange(event) {
  const { start, end, allDay } = event;
  const dayOpts = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
  const timeOpts = { hour: 'numeric', minute: '2-digit' };
  if (allDay) {
    // All-day end is exclusive — the last day shown is the day before it.
    const last = new Date(end.getTime() - 1);
    return start.toDateString() === last.toDateString()
      ? `${start.toLocaleDateString('en-US', dayOpts)} · All day`
      : `${start.toLocaleDateString('en-US', dayOpts)} – ${last.toLocaleDateString('en-US', dayOpts)}`;
  }
  if (start.toDateString() === end.toDateString()) {
    const endTime = end > start ? ` – ${end.toLocaleTimeString('en-US', timeOpts)}` : '';
    return `${start.toLocaleDateString('en-US', dayOpts)} · ${start.toLocaleTimeString('en-US', timeOpts)}${endTime}`;
  }
  return `${start.toLocaleString('en-US', { ...dayOpts, ...timeOpts })} – ${end.toLocaleString('en-US', { ...dayOpts, ...timeOpts })}`;
}

// Turn bare URLs in the description into clickable links (meeting links etc.).
function Linkified({ text }) {
  const parts = text.split(/(https?:\/\/[^\s<>"]+)/g);
  return parts.map((part, i) => (
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: SLATE, wordBreak: 'break-all' }}>{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

function Row({ Icon, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
      <Icon size={16} strokeWidth={2} color={SLATE} style={{ flexShrink: 0, marginTop: '3px' }} />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

export default function EventDetailModal({ event, isOpen, onClose, onDelete }) {
  if (!event) return null;
  const mapsUrl = event.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={event.title} maxWidth="520px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Row Icon={CalendarClock}>{formatRange(event)}</Row>

        {event.location && (
          <Row Icon={MapPin}>
            {event.location}
            <a
              href={mapsUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginLeft: '8px', fontSize: '12px', fontWeight: '600', color: SLATE }}
            >
              Map <ExternalLink size={11} strokeWidth={2} />
            </a>
          </Row>
        )}

        {event.description && (
          <Row Icon={FileText}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '260px', overflowY: 'auto', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <Linkified text={event.description} />
            </div>
          </Row>
        )}

        {event.sourceFile && (
          <div style={{ fontSize: '11px', color: STONE }}>Imported from {event.sourceFile}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '6px' }}>
          <Button variant="secondary" size="small" onClick={() => onDelete(event)}>
            <Trash2 size={13} strokeWidth={2} style={{ marginRight: '4px', verticalAlign: '-2px' }} />
            Remove from calendar
          </Button>
          <Button size="small" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}
